import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useCoupleStore } from "@/stores/couple";
import { needsWelcomeVideo, clearWelcomeVideoProgress } from "@/utils/welcomeVideo";

const routes = [
  {
    path: "/",
    name: "login",
    component: () => import("@/pages/LoginPage.vue"),
    meta: { public: true, guestOnly: true },
  },
  {
    path: "/login",
    redirect: { name: "login" },
  },
  {
    path: "/cadastro",
    name: "register",
    component: () => import("@/pages/RegisterPage.vue"),
    meta: { public: true, guestOnly: true },
  },
  {
    path: "/ativar-conta",
    name: "activate-account",
    component: () => import("@/pages/ActivateAccountPage.vue"),
    meta: { public: true, guestOnly: true },
  },
  {
    path: "/convite/:token",
    name: "invite",
    component: () => import("@/pages/InvitePage.vue"),
    meta: { public: true },
  },
  {
    path: "/app/super_admin",
    component: () => import("@/layouts/AdminShell.vue"),
    meta: { requiresAuth: true, requiresSuperAdmin: true },
    children: [
      { path: "", redirect: { name: "super-admin-users" } },
      {
        path: "usuarios",
        name: "super-admin-users",
        component: () => import("@/pages/admin/AdminUsersPage.vue"),
      },
      {
        path: "usuarios/:id",
        name: "super-admin-user-detail",
        component: () => import("@/pages/admin/AdminUserDetailPage.vue"),
      },
      {
        path: "casais",
        name: "super-admin-couples",
        component: () => import("@/pages/admin/AdminCouplesPage.vue"),
      },
      {
        path: "casais/:id",
        name: "super-admin-couple-detail",
        component: () => import("@/pages/admin/AdminCoupleDetailPage.vue"),
      },
      {
        path: "vsl-retencao",
        name: "super-admin-vsl-retention",
        component: () => import("@/pages/admin/AdminVslRetentionPage.vue"),
      },
    ],
  },
  {
    path: "/super_admin/:pathMatch(.*)*",
    redirect: (to) => {
      const rest = to.params.pathMatch;
      if (!rest || (Array.isArray(rest) && rest.length === 0)) return "/app/super_admin";
      const segment = Array.isArray(rest) ? rest.join("/") : rest;
      return `/app/super_admin/${segment}`;
    },
  },
  {
    path: "/app",
    component: () => import("@/layouts/AppShell.vue"),
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        name: "dashboard",
        component: () => import("@/pages/DashboardPage.vue"),
        meta: { pageTitleKey: "nav.today" },
      },
      {
        path: "criar-casal",
        name: "create-couple",
        component: () => import("@/pages/CreateCouplePage.vue"),
        meta: { fullscreen: true },
      },
      {
        path: "convite",
        name: "couple-invite",
        component: () => import("@/pages/CoupleInvitePage.vue"),
        meta: { fullscreen: true },
      },
      {
        path: "boas-vindas",
        name: "welcome",
        component: () => import("@/pages/WelcomePage.vue"),
        meta: { fullscreen: true },
      },
      {
        path: "diagnostico",
        name: "diagnostic",
        component: () => import("@/pages/DiagnosticPage.vue"),
        meta: { pageTitleKey: "diagnostic.title" },
      },
      {
        path: "jornada",
        name: "journey-overview",
        component: () => import("@/pages/JourneyOverviewPage.vue"),
        meta: { pageTitleKey: "journey.overview.title" },
      },
      {
        path: "jornada/semana/:n",
        name: "journey-week",
        component: () => import("@/pages/JourneyWeekPage.vue"),
      },
      {
        path: "jornada/encontros/:weekId",
        name: "journey-encounter",
        component: () => import("@/pages/EncounterPage.vue"),
      },
      {
        path: "jornada/mapa",
        name: "journey-map",
        component: () => import("@/pages/JourneyMapPage.vue"),
        meta: { pageTitleKey: "journeyMap.title" },
      },
      {
        path: "tarefas",
        redirect: (to) => ({ name: "journey-week", params: { n: to.query.n || 1 } }),
      },
      {
        path: "momentos/:taskId/roleta",
        name: "roulette",
        component: () => import("@/pages/RoulettePage.vue"),
        meta: { fullscreen: true },
      },
      {
        path: "checkin",
        name: "checkin-current",
        component: () => import("@/pages/CheckinPage.vue"),
        meta: { pageTitleKey: "checkin.title" },
      },
      {
        path: "checkin/:id",
        name: "checkin",
        component: () => import("@/pages/CheckinPage.vue"),
      },
      {
        path: "termometro",
        name: "thermometer",
        component: () => import("@/pages/ThermometerPage.vue"),
        meta: { pageTitleKey: "thermometer.title" },
      },
      {
        path: "resumo-semanal/:id",
        name: "weekly-summary",
        component: () => import("@/pages/WeeklySummaryPage.vue"),
        meta: { pageTitleKey: "aiSummary.title" },
      },
      {
        path: "combinados",
        name: "agreements",
        component: () => import("@/pages/AgreementsPage.vue"),
        meta: { pageTitleKey: "agreements.title" },
      },
      {
        path: "rituais",
        name: "rituals",
        component: () => import("@/pages/RitualsPage.vue"),
        meta: { pageTitleKey: "rituals.title" },
      },
      {
        path: "conversa-dificil",
        name: "difficult-conversation",
        component: () => import("@/pages/DifficultConversationPage.vue"),
        meta: { pageTitleKey: "difficultConversation.title" },
      },
      {
        path: "historico",
        name: "history",
        component: () => import("@/pages/HistoryPage.vue"),
        meta: { pageTitleKey: "history.title" },
      },
      {
        path: "manutencao",
        name: "maintenance",
        component: () => import("@/pages/MaintenancePage.vue"),
        meta: { pageTitleKey: "maintenance.title" },
      },
      {
        path: "mapa-do-mes/:cycleId",
        redirect: { name: "journey-map" },
      },
      {
        path: "configuracoes",
        name: "settings",
        component: () => import("@/pages/SettingsPage.vue"),
        meta: { pageTitleKey: "settings.title" },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  if (import.meta.env.DEV && to.query.revalidar === "1") {
    clearWelcomeVideoProgress();
  }

  if (!authStore.initialized) {
    await authStore.initialize();
  }

  const isAuthenticated = authStore.isAuthenticated;
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const requiresSuperAdmin = to.matched.some((record) => record.meta.requiresSuperAdmin);
  const guestOnly = to.meta.guestOnly;

  if (requiresAuth && !isAuthenticated) {
    next({ name: "login", query: { redirect: to.fullPath } });
    return;
  }

  if (requiresSuperAdmin && !authStore.isSuperAdmin) {
    next({ name: "dashboard" });
    return;
  }

  if (guestOnly && isAuthenticated) {
    next({ name: "dashboard" });
    return;
  }

  if (requiresAuth && isAuthenticated && !requiresSuperAdmin) {
    const coupleStore = useCoupleStore();
    if (!coupleStore.couple) {
      await coupleStore.fetchCurrent();
    }

    const onboardingNames = new Set(["create-couple", "couple-invite", "welcome"]);

    if (needsWelcomeVideo(coupleStore) && !onboardingNames.has(to.name)) {
      next({ name: "welcome" });
      return;
    }
  }

  next();
});

export default router;
